import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { axiosClient, getBaseUrlVersioned } from "../../services/axiosClient";
import { getCharacterById } from "../character/characterSlice";
import { getCharacterMessagesThunk } from "../conversation/conversationSlice";
import { createPersona, getPersonaById, getPersonas, updatePersona, updatePersonaIdInConversation } from "../user/userPersona/personaSlice";
import { addConversationRulebookCases } from "./conversationRulebookThunks";
export const updateConversationRulebookSettings = createAsyncThunk("rulebook/updateConversationRulebookSettings", async ({ conversationId, settings }, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(null)}/conversations/${conversationId}/rulebook`;
        const response = await axiosClient.patch(apiUrl, settings);
        const previous = thunkAPI.getState().rulebook.settingsByConversationId[conversationId] ?? {};
        return { conversationId, settings: response.data.result?.rulebook ?? { ...previous, ...settings } };
    }
    catch (error) {
        throw thunkAPI.rejectWithValue(error?.response?.data);
    }
});
const initialState = {
    byCharacterId: {},
    initialStateByCharacterId: {},
    byPersonaId: {},
    initialStateByPersonaId: {},
    byConversationId: {},
    initialStateByConversationId: {},
    settingsByConversationId: {},
    settingsRollbackByRequestId: {},
    settingsSeq: {},
    settingsConfirmedSeqByKey: {},
    personaIdByConversationId: {},
};
// The persona payloads embed their Rulebook HUD (payload or null); an absent field leaves any cached entry alone.
const hydratePersonaRulebook = (state, persona) => {
    if (!persona?.id || persona.rulebook_hud === undefined) {
        return;
    }
    state.byPersonaId[persona.id] = persona.rulebook_hud?.rulebook ?? null;
    state.initialStateByPersonaId[persona.id] = persona.rulebook_hud?.initial_state ?? null;
};
const hydrateConversationHud = (state, conversationId, rulebookHud) => {
    state.byConversationId[conversationId] = rulebookHud?.rulebook ?? null;
    state.initialStateByConversationId[conversationId] = rulebookHud?.initial_state ?? null;
};
// A locally pinned conversation must not be regressed by a stale messages/settings payload (a GET
// issued before the pin resolved): once `rulebook_id` is pinned, only a payload naming the same
// rulebook may hydrate the conversation's rulebook state.
const isStaleRulebookHydration = (state, conversationId, incomingRulebookId) => {
    const pinnedId = state.settingsByConversationId[conversationId]?.rulebook_id;
    return pinnedId !== undefined && incomingRulebookId !== pinnedId;
};
// A paginated page carries no HUD but still names the conversation's rulebook: if the cached HUD is
// for a different rulebook (an attach/detach raced the first page), the cached entry is stale.
const isCachedHudForOtherRulebook = (state, conversationId, incomingRulebookId) => {
    const cached = state.byConversationId[conversationId];
    return incomingRulebookId !== undefined && cached != null && cached.id !== incomingRulebookId;
};
// A messages GET issued before an unpinned persona switch may resolve with the previous persona's
// HUD: drop it. A pinned rulebook is persona-independent, so the pin wins over the persona check.
const isStalePersonaHydration = (state, conversationId, payloadPersonaId, pinnedRulebookId) => {
    const confirmedPersonaId = state.personaIdByConversationId[conversationId];
    return !pinnedRulebookId && confirmedPersonaId !== undefined && payloadPersonaId !== confirmedPersonaId;
};
// Re-apply still-in-flight patches per key: only requests newer than the latest confirmed
// fulfillment for that key may re-apply — a stale pending patch must not override a key a newer
// request already confirmed.
const reapplyInFlight = (state, conversationId, target) => {
    const confirmed = state.settingsConfirmedSeqByKey[conversationId] ?? {};
    Object.values(state.settingsRollbackByRequestId).forEach((inFlight) => {
        if (inFlight.conversationId === conversationId) {
            Object.entries(inFlight.patch).forEach(([key, value]) => {
                if (inFlight.seq > (confirmed[key] ?? 0)) {
                    target[key] = value;
                }
            });
        }
    });
};
// Re-apply every still-in-flight patch for the conversation on top of a new base, so an optimistic
// value is never lost when a server payload (fulfill or hydration) overwrites the cached settings.
const applyInFlightPatches = (state, conversationId, base) => {
    const merged = { ...base };
    reapplyInFlight(state, conversationId, merged);
    return merged;
};
// Switching persona re-resolves the conversation's rulebook: drop the cached HUD unless the
// server-side pin (from the PATCH response, or an earlier local one) says it survives.
const handlePersonaSwitch = (state, conversationId, personaId, rulebook) => {
    state.personaIdByConversationId[conversationId] = personaId;
    const serverPinnedId = rulebook?.rulebook_id;
    if (rulebook) {
        state.settingsByConversationId[conversationId] = applyInFlightPatches(state, conversationId, rulebook);
    }
    if (serverPinnedId ?? state.settingsByConversationId[conversationId]?.rulebook_id) {
        return;
    }
    delete state.byConversationId[conversationId];
    delete state.initialStateByConversationId[conversationId];
};
export const rulebookSlice = createSlice({
    name: "rulebook",
    initialState: initialState,
    reducers: {
        // Hydration entry point for consumers that load the conversation without getCharacterMessagesThunk (e.g. web useConversation).
        setConversationRulebookSettings: (state, action) => {
            if (isStaleRulebookHydration(state, action.payload.conversationId, action.payload.settings.rulebook_id)) {
                return;
            }
            state.settingsByConversationId[action.payload.conversationId] = applyInFlightPatches(state, action.payload.conversationId, action.payload.settings);
        },
        // Hydration entry point for consumers that load messages without getCharacterMessagesThunk (web jsonApiService).
        hydrateConversationRulebookHud: (state, action) => {
            if (isStaleRulebookHydration(state, action.payload.conversationId, action.payload.rulebook?.rulebook_id)) {
                return;
            }
            if (isStalePersonaHydration(state, action.payload.conversationId, action.payload.personaId, action.payload.rulebook?.rulebook_id)) {
                return;
            }
            hydrateConversationHud(state, action.payload.conversationId, action.payload.rulebookHud);
        },
    },
    extraReducers: (builder) => {
        // The character payload embeds its Rulebook (or none): hydrate from it so the HUD needs no dedicated fetch.
        builder.addCase(getCharacterById.fulfilled, (state, action) => {
            // Loads without ?include=rulebook carry no `rulebook` at all — leave any cached entry alone.
            if (!action.meta.arg.include?.includes("rulebook") || !action.payload?.id) {
                return;
            }
            state.byCharacterId[action.payload.id] = action.payload.rulebook?.rulebook ?? null;
            state.initialStateByCharacterId[action.payload.id] = action.payload.rulebook?.initial_state ?? null;
        });
        // The persona payloads embed their Rulebook HUD on personas that carry a rulebook_id.
        builder.addCase(getPersonas.fulfilled, (state, action) => {
            if (!Array.isArray(action.payload)) {
                return;
            }
            action.payload.forEach((persona) => hydratePersonaRulebook(state, persona));
        });
        builder.addCase(getPersonaById.fulfilled, (state, action) => hydratePersonaRulebook(state, action.payload));
        builder.addCase(createPersona.fulfilled, (state, action) => hydratePersonaRulebook(state, action.payload));
        builder.addCase(updatePersona.fulfilled, (state, action) => hydratePersonaRulebook(state, action.payload));
        builder.addCase(updatePersonaIdInConversation.fulfilled, (state, action) => handlePersonaSwitch(state, action.meta.arg.conversationId, action.meta.arg.personaId, action.payload?.rulebook));
        // Hydrate conversation-level settings and the resolved Rulebook HUD from the conversation load.
        builder.addCase(getCharacterMessagesThunk.fulfilled, (state, action) => {
            const conversationId = action.payload.conversation_id;
            if (!conversationId) {
                return;
            }
            if (isStaleRulebookHydration(state, conversationId, action.payload.rulebook?.rulebook_id)) {
                return;
            }
            state.settingsByConversationId[conversationId] = applyInFlightPatches(state, conversationId, action.payload.rulebook ?? {});
            if (action.payload.rulebook_hud !== undefined) {
                if (!isStalePersonaHydration(state, conversationId, action.payload.userPersona?.id, action.payload.rulebook?.rulebook_id)) {
                    hydrateConversationHud(state, conversationId, action.payload.rulebook_hud);
                }
            }
            else if (isCachedHudForOtherRulebook(state, conversationId, action.payload.rulebook?.rulebook_id)) {
                // Paginated page naming a different rulebook than the cached HUD: hide it (null, not delete —
                // falling through to persona/character would resurrect the wrong rulebook).
                hydrateConversationHud(state, conversationId, null);
            }
        });
        builder.addCase(updateConversationRulebookSettings.pending, (state, action) => {
            const { conversationId, settings } = action.meta.arg;
            const current = state.settingsByConversationId[conversationId] ?? {};
            // Stash the previous values of just the patched keys (undefined marks an absent key) plus the
            // patch itself, so a later success isn't clobbered by an earlier rejection and vice versa.
            const previous = {};
            for (const key of Object.keys(settings)) {
                previous[key] = current[key];
            }
            const seq = (state.settingsSeq[conversationId] ?? 0) + 1;
            state.settingsSeq[conversationId] = seq;
            state.settingsRollbackByRequestId[action.meta.requestId] = { conversationId, previous, patch: settings, seq };
            state.settingsByConversationId[conversationId] = { ...current, ...settings };
        });
        builder.addCase(updateConversationRulebookSettings.fulfilled, (state, action) => {
            const { conversationId, settings } = action.payload;
            const entry = state.settingsRollbackByRequestId[action.meta.requestId];
            delete state.settingsRollbackByRequestId[action.meta.requestId];
            const seq = entry?.seq ?? 0;
            state.settingsConfirmedSeqByKey[conversationId] ??= {};
            const confirmed = state.settingsConfirmedSeqByKey[conversationId];
            // Server response is authoritative for the keys it returns — except keys a newer request
            // already confirmed (out-of-order fulfill).
            const merged = { ...(state.settingsByConversationId[conversationId] ?? {}) };
            Object.entries(settings).forEach(([key, value]) => {
                if (seq >= (confirmed[key] ?? 0)) {
                    if (value === undefined) {
                        delete merged[key];
                    }
                    else {
                        merged[key] = value;
                    }
                }
            });
            // This request confirms the keys it patched when it is the newest fulfillment for them.
            Object.keys(entry?.patch ?? {}).forEach((key) => {
                if (seq >= (confirmed[key] ?? 0)) {
                    confirmed[key] = seq;
                }
            });
            // Re-apply still-in-flight patches per key (same rule as hydration).
            reapplyInFlight(state, conversationId, merged);
            state.settingsByConversationId[conversationId] = merged;
        });
        builder.addCase(updateConversationRulebookSettings.rejected, (state, action) => {
            const rollback = state.settingsRollbackByRequestId[action.meta.requestId];
            delete state.settingsRollbackByRequestId[action.meta.requestId];
            if (!rollback) {
                return;
            }
            const restored = { ...(state.settingsByConversationId[rollback.conversationId] ?? {}) };
            const confirmed = state.settingsConfirmedSeqByKey[rollback.conversationId] ?? {};
            for (const key of Object.keys(rollback.patch)) {
                // Skip restoring a key a newer in-flight request also patches, or one a newer request already
                // confirmed — the newer request owns that key's value now.
                const hasNewerInFlight = Object.values(state.settingsRollbackByRequestId).some((inFlight) => inFlight.conversationId === rollback.conversationId && inFlight.seq > rollback.seq && key in inFlight.patch);
                if (hasNewerInFlight || (confirmed[key] ?? 0) >= rollback.seq) {
                    continue;
                }
                if (rollback.previous[key] === undefined) {
                    delete restored[key];
                }
                else {
                    restored[key] = rollback.previous[key];
                }
            }
            state.settingsByConversationId[rollback.conversationId] = restored;
        });
        addConversationRulebookCases(builder);
    },
});
export const { setConversationRulebookSettings, hydrateConversationRulebookHud } = rulebookSlice.actions;
export default rulebookSlice.reducer;
